// A file picker drawn as a dropzone (spec/14 §6): the catalogue's icon, trailer and Add app uploads. The native
// `<input type="file">` renders as "Choose File · No file chosen", which reads as neither a control nor part of
// the product, so it sits visually hidden inside a label drawn as a dashed well — an upload glyph, what it
// takes, the size cap — that is clickable, keyboard-focusable (the ring is drawn on the well) and accepts a
// dragged file. It shows the chosen file's name, and a progress bar while an upload runs.
//
// The input keeps its accessible name (`label`), so a screen reader and a test reach it as before.

import { cx, ProgressBar } from '@clavian/ui-web';
import { useState, type ReactNode, type Ref } from 'react';

import { Icon } from '../../shell/nav-icons';

import './media.css';

export interface UploadDropProps {
  /** The input's accessible name — "Icon file", "Video file". */
  readonly label: string;
  /** The well's headline — "Upload PNG or WebP". */
  readonly title: string;
  /** The line under it — how to use it and the size cap. */
  readonly hint: string;
  readonly accept: string;
  readonly disabled?: boolean;
  /** The chosen (or uploading) file's name, shown in place of the hint. */
  readonly fileName?: string | null;
  /** 0–1 while an upload runs; `null` otherwise. */
  readonly progress?: number | null;
  /** The progress bar's accessible name while uploading. */
  readonly progressLabel?: string;
  readonly onFile: (file: File | null) => void;
  /** Lets a caller clear the input after a pick, so the same file can be chosen twice. */
  readonly inputRef?: Ref<HTMLInputElement>;
  readonly compact?: boolean;
}

export function UploadDrop({ label, title, hint, accept, disabled = false, fileName = null, progress = null, progressLabel, onFile, inputRef, compact = false }: UploadDropProps): ReactNode {
  const [over, setOver] = useState(false);
  return (
    <div className="console-upload-drop__frame">
      <label
        className={cx('console-upload-drop', over && 'console-upload-drop--over', disabled && 'console-upload-drop--disabled', compact && 'console-upload-drop--compact')}
        onDragOver={(event) => {
          if (disabled) return;
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          if (disabled) return;
          onFile(event.dataTransfer.files[0] ?? null);
        }}
      >
        <input
          ref={inputRef}
          className="console-upload-drop__input"
          type="file"
          accept={accept}
          disabled={disabled}
          aria-label={label}
          onChange={(event) => {
            onFile(event.target.files?.[0] ?? null);
            event.target.value = '';
          }}
        />
        <span className="console-upload-drop__glyph" aria-hidden="true">
          <Icon name="upload" />
        </span>
        <span className="console-upload-drop__text">
          <span className="console-upload-drop__title">{progress !== null && progressLabel !== undefined ? progressLabel : title}</span>
          <span className="console-upload-drop__hint">{fileName ?? hint}</span>
        </span>
      </label>
      {progress !== null ? <ProgressBar value={progress} label={progressLabel ?? label} size="sm" /> : null}
    </div>
  );
}
